import React, { useCallback, useState } from 'react';
import {
  Card,
  InlineStack,
  BlockStack,
  Text,
  ProgressBar,
  Banner,
  Button,
  Modal,
  Divider,
  Box,
} from '@shopify/polaris';
import { AlertCircleIcon } from '@shopify/polaris-icons';
import type { GlobalCap } from '../types';
import { SUB_SCORE_FIELDS, getScoreTier } from '../theme';

/**
 * T048: SubScoreBreakdown component.
 * Displays 6 sub-score bars with labels, weights, individual colours,
 * and global cap warnings. A single help icon opens a drawer-style modal
 * explaining every sub-score.
 *
 * @see documents/ui-enhancement-spec.md §10, §13
 */

interface SubScores {
  dataCompleteness: number;
  schemaQuality: number;
  identifierTaxonomy: number;
  llmTextQuality: number;
  mediaQuality: number;
  internalConsistency: number;
}

interface SubScoreBreakdownProps {
  subScores: SubScores;
  globalCaps?: GlobalCap[];
  /** When true, a "Fix" link is shown on the Schema Quality row if its score is 0. */
  isPaidTier?: boolean;
  /** Invoked when the user clicks the Schema Quality "Fix" link. */
  onFixSchemaQuality?: () => void;
}

function scoreToPolarisTone(score: number): 'success' | 'highlight' | 'critical' {
  const tier = getScoreTier(score);
  switch (tier) {
    case 'excellent':
    case 'good':
      return 'success';
    case 'warning':
      return 'highlight';
    case 'critical':
      return 'critical';
  }
}

export function SubScoreBreakdown({
  subScores,
  globalCaps = [],
  isPaidTier = false,
  onFixSchemaQuality,
}: SubScoreBreakdownProps): React.ReactElement {
  const [helpOpen, setHelpOpen] = useState(false);
  const toggleHelp = useCallback(() => setHelpOpen((o) => !o), []);

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              Score Breakdown
            </Text>
            <Button variant="plain" onClick={toggleHelp}>
              how to fix?
            </Button>
          </InlineStack>

          {globalCaps.length > 0 && (
            <BlockStack gap="200">
              {globalCaps.map((cap, idx) => (
                <Banner key={idx} tone="warning" icon={AlertCircleIcon}>
                  <Text as="p" variant="bodySm">
                    {cap.reason} (capped at {cap.cappedAt})
                  </Text>
                </Banner>
              ))}
            </BlockStack>
          )}

          <BlockStack gap="300">
            {SUB_SCORE_FIELDS.map(({ key, label }) => {
              const score = Math.round(subScores[key as keyof SubScores]);
              const tone = scoreToPolarisTone(score);

              const isSchema = key === 'schemaQuality' && !!onFixSchemaQuality;
              const showFix = isSchema && score === 0 && isPaidTier;
              const showFeedSettings = isSchema && score > 0 && score < 100;

              return (
                <BlockStack key={key} gap="100">
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodySm" fontWeight="medium">
                      {label}
                    </Text>
                    <InlineStack gap="200" blockAlign="center">
                      {showFix && (
                        <Button variant="plain" onClick={onFixSchemaQuality}>
                          Fix
                        </Button>
                      )}
                      {showFeedSettings && (
                        <Button variant="plain" onClick={onFixSchemaQuality}>
                          feed settings
                        </Button>
                      )}
                      <Text
                        as="span"
                        variant="bodySm"
                        fontWeight="semibold"
                        tone={tone === 'critical' ? 'critical' : undefined}
                      >
                        {score}
                      </Text>
                    </InlineStack>
                  </InlineStack>
                  <ProgressBar progress={score} size="small" tone={tone} />
                </BlockStack>
              );
            })}
          </BlockStack>
        </BlockStack>
      </Card>

      {/* Help drawer — explains every sub-score */}
      <Modal open={helpOpen} onClose={toggleHelp} title="How Your Score Is Calculated">
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p" variant="bodyMd">
              Your AI Discoverability Score is a weighted average of 6 sub-scores. Each pillar
              measures a different aspect of how well AI assistants can discover, understand, and
              recommend your products.
            </Text>

            <Divider />

            {SUB_SCORE_FIELDS.map(({ label, description }, idx) => (
              <React.Fragment key={label}>
                <BlockStack gap="100">
                  <Text as="h3" variant="headingSm">
                    {label}
                  </Text>
                  <Box paddingInlineStart="0">
                    <Text as="p" variant="bodyMd" tone="subdued">
                      {description}
                    </Text>
                  </Box>
                </BlockStack>
                {idx < SUB_SCORE_FIELDS.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
